import type {
    CrmFieldType,
    ExcludeAccountFields,
    HubspotMappingField,
} from "app/pages/sales-intelligence/common-components/export-table/types";
import { createCancelable } from "app/pages/workspace/sales/helpers";
import type { IFetchService } from "app/services/fetchService";
import type { HubspotPreDefinedType } from "../types/shared";
import { ExportSettingsSourceType } from "../types/shared";
import type { HubspotPredefinedApiServiceType } from "./hubspot/predefined";
import { hubspotPredefinedApiService } from "./hubspot/predefined";
import type { HubspotStatusServiceType } from "./hubspot/status";
import { hubspotStatusService } from "./hubspot/status";
import type { HubspotTogglesServiceType } from "./hubspot/toggles";
import { hubspotTogglesService } from "./hubspot/toggles";

export type HubspotServiceType = {
    getHsFields(
        columnName: string,
        type: HubspotPreDefinedType,
        cacheToken: string,
        sourceType?: ExportSettingsSourceType,
    ): Promise<CrmFieldType[]>;
    getHsColumns(type: HubspotPreDefinedType, cacheToken: string, cancellation: AbortSignal): Promise<CrmFieldType[]>;
    getHsExcludeAccountFields(): Promise<ExcludeAccountFields>;
    getHsMappingFields(): Promise<HubspotMappingField[]>;
    putHsStatusFields(externalField: string): Promise<{ value: boolean }>;
    putHsFilterFields(fields: string[]): Promise<{ value: boolean }>;
};

export const hubspotService = (
    fetchService: IFetchService,
): HubspotServiceType & HubspotStatusServiceType & HubspotTogglesServiceType & HubspotPredefinedApiServiceType => ({
    getHsFields: createCancelable(
        (
            signal: AbortSignal,
            columnName: string,
            type: HubspotPreDefinedType,
            cacheToken: string,
            sourceType?: ExportSettingsSourceType,
        ): Promise<CrmFieldType[]> => {
            const params = new URLSearchParams();

            params.set("column", sourceType === ExportSettingsSourceType.Technologies ? "techCategory" : columnName);
            sourceType && params.set("sourceType", sourceType.toString());
            params.set("cacheToken", cacheToken);
            const url = `sales-api/hubspot/${type}-applicable-columns?${params.toString()}`;

            return fetchService.get(url, undefined, {
                cancellation: signal,
            });
        },
    ),
    getHsColumns(type: HubspotPreDefinedType, cacheToken: string, cancellation: AbortSignal) {
        return fetchService.get<CrmFieldType[]>(
            `sales-api/hubspot/${type}-columns?cacheToken=${cacheToken}`,
            undefined,
            { cancellation },
        );
    },
    getHsExcludeAccountFields: (): Promise<ExcludeAccountFields> => {
        return fetchService.get("sales-api/hubspot/account-text-columns");
    },
    getHsMappingFields: createCancelable((signal: AbortSignal): Promise<HubspotMappingField[]> => {
        return fetchService.get("sales-api/hubspot/mapping-field?type=AccountImport", undefined, {
            cancellation: signal,
        });
    }),
    putHsStatusFields: createCancelable((signal: AbortSignal, externalField: string): Promise<{ value: boolean }> => {
        return fetchService.put(
            `sales-api/hubspot/mapping-field/import/type?hubspotField=${externalField}`,
            undefined,
            { cancellation: signal },
        );
    }),
    putHsFilterFields: createCancelable((signal: AbortSignal, fields: string[]): Promise<{ value: boolean }> => {
        return fetchService.put("sales-api/hubspot/mapping-field/import/lists", fields, {
            cancellation: signal,
        });
    }),

    ...hubspotStatusService(fetchService),
    ...hubspotTogglesService(fetchService),
    ...hubspotPredefinedApiService(fetchService),
});
